"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SExpression = exports.List = exports.Struct = exports.Clob = exports.Blob = exports.Symbol = exports.String = exports.Timestamp = exports.Float = exports.Decimal = exports.Integer = exports.Boolean = exports.Null = exports.Value = exports.load = exports.loadAll = void 0;
const IntSize_1 = __importDefault(require("../IntSize"));
const IonBinaryReader_1 = require("../IonBinaryReader");
const IonTextReader_1 = require("../IonTextReader");
const IonTypes_1 = require("../IonTypes");
const Blob_1 = require("./Blob");
const Clob_1 = require("./Clob");
const Decimal_1 = require("./Decimal");
const Integer_1 = require("./Integer");
const SExpression_1 = require("./SExpression");
const Symbol_1 = require("./Symbol");
const Timestamp_1 = require("./Timestamp");
const Boolean_1 = require("./Boolean");
const String_1 = require("./String");
const makeReader_1 = require("../makeReader");
const JsValueConversion_1 = require("./JsValueConversion");
const FromJsConstructor_1 = require("./FromJsConstructor");
/**
 * Reads the provided Ion data source into memory, constructing Value objects to represent
 * each value found in the stream.
 *
 * This approach to reading is less efficient at runtime than using the streaming Reader API
 * (next(), stepIn(), stepOut(), etc), but is often much simpler to use.
 *
 * If `ionData` is a ReaderBuffer, a new Reader will be created to process it.
 * If it is already a Reader, it will be consumed fully.
 *
 * If `ionData` does not contain any values, an empty array will be returned.
 *
 * @param ionData   A source of Ion data (text or binary) to load into memory.
 * @returns         An array of Value objects representing the values found in the stream.
 */
function loadAll(ionData) {
    const reader = _createReader(ionData);
    const ionValues = [];
    while (reader.next()) {
        ionValues.push(_loadValue(reader));
    }
    return ionValues;
}
exports.loadAll = loadAll;
/**
 * Reads the first value from the provided Ion data source into memory, constructing
 * a Value object to represent it.
 *
 * This approach to reading is less efficient at runtime than using the streaming Reader API
 * (next(), stepIn(), stepOut(), etc), but is often much simpler to use.
 *
 * If `ionData` is a ReaderBuffer, a new Reader will be created to process it.
 * If `ionData` is already a Reader, it will be used as-is.
 *
 * `load` will check to see if the Reader is already positioned over a value. If it is,
 * that value will be loaded. If it is not, `load` will call `next()` once.
 *
 * If no value is found, `load` will return null.
 *
 * @param ionData   A source of Ion data (text or binary) to load into memory.
 * @returns         A Value object representing the first value found in the stream
 *                  or null if the stream is empty.
 */
function load(ionData) {
    const reader = _createReader(ionData);
    if (reader.type() === null) {
        reader.next();
    }
    return reader.type() === null ? null : _loadValue(reader);
}
exports.load = load;
function _createReader(ionData) {
    // If the provided parameter is already a reader, no new work is required.
    // However, we cannot simply test `ionData instanceof Reader` because `Reader`
    // is an interface.
    if (ionData instanceof IonTextReader_1.TextReader || ionData instanceof IonBinaryReader_1.BinaryReader) {
        return ionData;
    }
    return (0, makeReader_1.makeReader)(ionData);
}
// Loads the Reader's current value, returning it as a DOM Value
function _loadValue(reader) {
    const ionType = reader.type();
    if (ionType === null) {
        throw new Error("loadValue() called when no further values were available to read.");
    }
    const annotations = reader.annotations();
    if (reader.isNull()) {
        return new FromJsConstructor_1.Null(reader.type(), annotations);
    }
    switch (ionType) {
        case IonTypes_1.IonTypes.NULL:
            return new FromJsConstructor_1.Null(IonTypes_1.IonTypes.NULL, annotations);
        case IonTypes_1.IonTypes.BOOL:
            return new Boolean_1.Boolean(reader.booleanValue(), annotations);
        case IonTypes_1.IonTypes.INT:
            return reader.intSize() == IntSize_1.default.Number
                ? new Integer_1.Integer(reader.numberValue(), annotations)
                : new Integer_1.Integer(reader.bigIntValue(), annotations);
        case IonTypes_1.IonTypes.FLOAT:
            return new Decimal_1.Float(reader.numberValue(), annotations);
        case IonTypes_1.IonTypes.DECIMAL:
            return new Decimal_1.Decimal(reader.decimalValue(), annotations);
        case IonTypes_1.IonTypes.TIMESTAMP:
            return new Timestamp_1.Timestamp(reader.timestampValue(), annotations);
        case IonTypes_1.IonTypes.SYMBOL:
            return new Symbol_1.Symbol(reader.stringValue(), annotations);
        case IonTypes_1.IonTypes.STRING:
            return new String_1.String(reader.stringValue(), annotations);
        case IonTypes_1.IonTypes.CLOB:
            return new Clob_1.Clob(reader.byteValue(), annotations);
        case IonTypes_1.IonTypes.BLOB:
            return new Blob_1.Blob(reader.byteValue(), annotations);
        // Containers
        case IonTypes_1.IonTypes.LIST:
            return _loadList(reader);
        case IonTypes_1.IonTypes.SEXP:
            return _loadSExpression(reader);
        case IonTypes_1.IonTypes.STRUCT:
            return _loadStruct(reader);
        default:
            throw new Error(`Unrecognized IonType '${ionType}' found.`);
    }
}
function _loadStruct(reader) {
    const children = new Map();
    const annotations = reader.annotations();
    reader.stepIn();
    while (reader.next()) {
        if (children.has(reader.fieldName())) {
            children.get(reader.fieldName()).push(_loadValue(reader));
        }
        else {
            children.set(reader.fieldName(), [_loadValue(reader)]);
        }
    }
    reader.stepOut();
    return new JsValueConversion_1.Struct(children.entries(), annotations);
}
function _loadList(reader) {
    const annotations = reader.annotations();
    return new JsValueConversion_1.List(_loadSequence(reader), annotations);
}
function _loadSExpression(reader) {
    const annotations = reader.annotations();
    return new SExpression_1.SExpression(_loadSequence(reader), annotations);
}
function _loadSequence(reader) {
    const children = [];
    reader.stepIn();
    while (reader.next()) {
        children.push(_loadValue(reader));
    }
    reader.stepOut();
    return children;
}
var Value_1 = require("./Value");
Object.defineProperty(exports, "Value", { enumerable: true, get: function () { return Value_1.Value; } });
var FromJsConstructor_2 = require("./FromJsConstructor");
Object.defineProperty(exports, "Null", { enumerable: true, get: function () { return FromJsConstructor_2.Null; } });
var Boolean_2 = require("./Boolean");
Object.defineProperty(exports, "Boolean", { enumerable: true, get: function () { return Boolean_2.Boolean; } });
var Integer_2 = require("./Integer");
Object.defineProperty(exports, "Integer", { enumerable: true, get: function () { return Integer_2.Integer; } });
var Decimal_2 = require("./Decimal");
Object.defineProperty(exports, "Decimal", { enumerable: true, get: function () { return Decimal_2.Decimal; } });
Object.defineProperty(exports, "Float", { enumerable: true, get: function () { return Decimal_2.Float; } });
var Timestamp_2 = require("./Timestamp");
Object.defineProperty(exports, "Timestamp", { enumerable: true, get: function () { return Timestamp_2.Timestamp; } });
var String_2 = require("./String");
Object.defineProperty(exports, "String", { enumerable: true, get: function () { return String_2.String; } });
var Symbol_2 = require("./Symbol");
Object.defineProperty(exports, "Symbol", { enumerable: true, get: function () { return Symbol_2.Symbol; } });
var Blob_2 = require("./Blob");
Object.defineProperty(exports, "Blob", { enumerable: true, get: function () { return Blob_2.Blob; } });
var Clob_2 = require("./Clob");
Object.defineProperty(exports, "Clob", { enumerable: true, get: function () { return Clob_2.Clob; } });
var JsValueConversion_2 = require("./JsValueConversion");
Object.defineProperty(exports, "Struct", { enumerable: true, get: function () { return JsValueConversion_2.Struct; } });
Object.defineProperty(exports, "List", { enumerable: true, get: function () { return JsValueConversion_2.List; } });
var SExpression_2 = require("./SExpression");
Object.defineProperty(exports, "SExpression", { enumerable: true, get: function () { return SExpression_2.SExpression; } });
